<header class="go-page-title">
  <h1 class="go-heading-1">{{ pageTitle }}</h1>
</header>
<div class="go-container">
  <section id="sass-color-variables" class="go-column go-column--100">
    <go-card>
      <ng-container go-card-header>
        <h2 class="go-heading-2 go-heading--no-wrap">SASS Color Variables</h2>
        <go-copy cardId="sass-color-variables" goCopyCardLink></go-copy>
      </ng-container>
      <ng-container go-card-content>
        <p class="go-body-copy">
          All of the available sass variables for theming, branding, & other
          UI states are available via the goponents package. 
          These can be imported in a scss file with: 
          <code class="code-block--inline">@import '~goponents/styles/variables';</code>
        </p>
      </ng-container>
    </go-card>
  </section>

  <section id="go-color-base" class="go-column go-column--100">
    <go-card>
      <ng-container go-card-header>
        <h2 class="go-heading-2 go-heading--no-wrap">Base Colors</h2>
        <go-copy cardId="go-color-base" goCopyCardLink></go-copy>
      </ng-container>
      <ng-container go-card-content>
        <section class="go-container">
          <div class="go-column go-column--50">
            <div class="go-color-items">
              <div id="go-color-primary" class="go-color-items__item">
                <span class="overline">#20A65F</span>
              </div>
              <div id="go-color-light" class="go-color-items__item go-color-items__item--dark">
                <span class="overline">#FFFFFF</span>
              </div>
              <div id="go-color-light-secondary" class="go-color-items__item go-color-items__item--dark">
                <span class="overline">#DEE2E6</span>
              </div>
              <div id="go-color-dark" class="go-color-items__item">
                <span class="overline">#313536</span>
              </div>
              <div id="go-color-dark-secondary" class="go-color-items__item">
                <span class="overline">#212529</span>
              </div>
            </div>
          </div>
          <div class="go-column go-column--50">
            <code [highlight]="baseColors"></code>
          </div>
        </section>
      </ng-container>
    </go-card>
  </section>

  <section id="go-color-primary" class="go-column go-column--100">
    <go-card>
      <ng-container go-card-header>
        <h2 class="go-heading-2 go-heading--no-wrap">Galaxy Blue</h2>
        <go-copy cardId="go-color-primary" goCopyCardLink></go-copy>
      </ng-container>
      <ng-container go-card-content>
        <section class="go-container">
          <div class="go-column go-column--50">
            <p class="go-body-copy">
              Galaxy Blue should be used when there is a primary action on a page or other
              minor accents in the UI.<br/>
              <caption>Base SASS variable: $ui-color-primary</caption>
            </p>
            <div class="go-color-items">
              <div class="go-color-items__item">
                <span class="overline">#157DB9</span>
              </div>
              <div class="go-color-items__item">
                <span class="overline">#1372a9</span>
              </div>
              <div class="go-color-items__item">
                <span class="overline">#116799</span>
              </div>
              <div class="go-color-items__item">
                <span class="overline">$ui-color-positive-gradient</span>
              </div>
            </div>
          </div>
          <div class="go-column go-column--50">
            <code [highlight]="galaxyBlue"></code>
          </div>
        </section>
      </ng-container>
    </go-card>
  </section>

  <section id="go-color-positive" class="go-column go-column--100">
    <go-card>
      <ng-container go-card-header>
        <h2 class="go-heading-2 go-heading--no-wrap">Earth Green</h2>
        <go-copy cardId="go-color-positive" goCopyCardLink></go-copy>
      </ng-container>
      <ng-container go-card-content>
        <section class="go-container">
          <div class="go-column go-column--50">
            <p class="go-body-copy">
              Earth Green should be used to indicate a positive event (ex: form completion), a positive status (ex: indicator),
               or to show a numerical positive (ex: currency). <br/>
              <caption>Base SASS variable: $ui-color-positive</caption>
            </p>
            <div class="go-color-items">
              <div class="go-color-items__item">
                <span class="overline">#198754</span>
              </div>
              <div class="go-color-items__item">
                <span class="overline">#16784b</span>
              </div>
              <div class="go-color-items__item">
                <span class="overline">#136941</span>
              </div>
              <div class="go-color-items__item">
                <span class="overline">$ui-color-positive-gradient</span>
              </div>
            </div>
          </div>
          <div class="go-column go-column--50">
            <code [highlight]="earthGreen"></code>
          </div>
        </section>
      </ng-container>
    </go-card>
  </section>
  
  <section id="go-color-negative" class="go-column go-column--100">
    <go-card>
      <ng-container go-card-header>
        <h2 class="go-heading-2 go-heading--no-wrap">Horizon Red</h2>
        <go-copy cardId="go-color-negative" goCopyCardLink></go-copy>
      </ng-container>
      <ng-container go-card-content>
        <section class="go-container">
          <div class="go-column go-column--50">
            <p class="go-body-copy">
              Horizon Red should be used when an action will result in a negative result (ex: deletion), when
              an event has resulted in an error (ex: failed form submission), a sense of urgency is needed (ex: alerts or warnings), or
              numerical negative (ex: currency).<br/>
              <caption>Base SASS variable: $ui-color-negative</caption>
            </p>
            <div class="go-color-items">
              <div class="go-color-items__item">
                <span class="overline">#DC3545</span>
              </div>
              <div class="go-color-items__item">
                <span class="overline">#d92637</span>
              </div>
              <div class="go-color-items__item">
                <span class="overline">#ca2333</span>
              </div>
              <div class="go-color-items__item">
                <span class="overline">$ui-color-negative-gradient</span>
              </div>
            </div>
          </div>
          <div class="go-column go-column--50">
            <code [highlight]="horizonRed"></code>
          </div>
        </section>
      </ng-container>
    </go-card>
  </section>

  <section id="go-color-neutral" class="go-column go-column--100">
    <go-card>
      <ng-container go-card-header>
        <h2 class="go-heading-2 go-heading--no-wrap">Nebula Purple</h2>
        <go-copy cardId="go-color-neutral" goCopyCardLink></go-copy>
      </ng-container>
      <ng-container go-card-content>
        <section class="go-container">
          <div class="go-column go-column--50">
            <p class="go-body-copy">
              Nebula Purple should be used to present additional information (ex: notice), or when taking a neutral action (ex: data refresh). <br/>
              <caption>Base SASS variable: $ui-color-neutral</caption>
            </p>
            <div class="go-color-items">
              <div class="go-color-items__item">
                <span class="overline">#6633CA</span>
              </div>
              <div class="go-color-items__item">
                <span class="overline">#5f2fbc</span>
              </div>
              <div class="go-color-items__item">
                <span class="overline">#582cad</span>
              </div>
              <div class="go-color-items__item">
                <span class="overline">$ui-color-neutral-gradient</span>
              </div>
            </div>
          </div>
          <div class="go-column go-column--50">
            <code [highlight]="nebulaPurple"></code>
          </div>
        </section>
      </ng-container>
    </go-card>
  </section>

  <section id="go-color-gray-scale" class="go-column go-column--100">
    <go-card>
      <ng-container go-card-header>
        <h2 class="go-heading-2 go-heading--no-wrap">Neutral Scale</h2>
        <go-copy cardId="go-color-gray-scale" goCopyCardLink></go-copy>
      </ng-container>
      <ng-container go-card-content>
        <section class="go-container">
          <div class="go-column go-column--50">
            <p class="go-body-copy">
              The neutral scale provides a consistent gray palette for backgrounds, borders, disabled states, and structural elements throughout the UI.<br/>
              <caption>Base SASS variables: $gray-100 through $gray-900</caption>
            </p>
            <div class="go-color-items go-color-items--compact">
              <div class="go-color-items__item go-color-items__item--dark go-color-items__item--third">
                <span class="overline">$gray-100</span>
              </div>
              <div class="go-color-items__item go-color-items__item--dark go-color-items__item--third">
                <span class="overline">$gray-200</span>
              </div>
              <div class="go-color-items__item go-color-items__item--dark go-color-items__item--third">
                <span class="overline">$gray-300</span>
              </div>
              <div class="go-color-items__item go-color-items__item--dark go-color-items__item--third">
                <span class="overline">$gray-400</span>
              </div>
              <div class="go-color-items__item go-color-items__item--dark go-color-items__item--third">
                <span class="overline">$gray-500</span>
              </div>
              <div class="go-color-items__item go-color-items__item--third">
                <span class="overline">$gray-600</span>
              </div>
              <div class="go-color-items__item go-color-items__item--third">
                <span class="overline">$gray-700</span>
              </div>
              <div class="go-color-items__item go-color-items__item--third">
                <span class="overline">$gray-800</span>
              </div>
              <div class="go-color-items__item go-color-items__item--third">
                <span class="overline">$gray-900</span>
              </div>
            </div>
          </div>
          <div class="go-column go-column--50">
            <code [highlight]="neutralScale"></code>
          </div>
        </section>
      </ng-container>
    </go-card>
  </section>
</div>
